import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { agrogestao } from "@/api/agrogestaoClient";

import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Wheat, TrendingUp, TrendingDown, DollarSign, BarChart3, Target, Scale, Layers } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from "recharts";

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function RelatorioRentabilidadeSafra({
  safras = [],
  lancamentos = [],
  custosAlocados = [],
  propriedade,
  filtros = {}
}) {
  const [safraSelecionada, setSafraSelecionada] = useState("todas");
  const propriedadeId = propriedade?.id || localStorage.getItem("propriedade_selecionada");


  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const renderPieLabel = ({ name, percent }) => {
    if (percent < 0.06) return null;
    return `${name} ${(percent * 100).toFixed(0)}%`;
  };

  const { data: movInsumos = [] } = useQuery({
    queryKey: ['mov_insumos', propriedadeId],
    queryFn: () => propriedadeId ? agrogestao.entities.MovimentacaoInsumo.filter({ propriedade_id: propriedadeId }) : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: movGraos = [] } = useQuery({
    queryKey: ['mov_graos', propriedadeId],
    queryFn: () => propriedadeId ? agrogestao.entities.MovimentacaoGrao.filter({ propriedade_id: propriedadeId }) : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  // Calcular rentabilidade por safra
  const rentabilidadeSafras = useMemo(() => {
    return safras.map(safra => {
      // Receitas financeiras
      const receitasF = lancamentos
        .filter(l => String(l.safra_id) === String(safra.id) && l.tipo === 'receita' && (l.status === 'pago' || !l.status))
        .reduce((acc, l) => acc + (l.valor || 0), 0);

      // Receitas de vendas de grãos (venda de estoque)
      const receitasG = movGraos
        .filter(m => String(m.safra_id) === String(safra.id) && m.tipo === 'saida')
        .reduce((acc, m) => acc + (Number(m.valor_total) || 0), 0);

      const receitas = receitasF + receitasG;

      // Despesas financeiras
      const despesasLancamentos = lancamentos
        .filter(l => String(l.safra_id) === String(safra.id) && l.tipo === 'despesa' && (l.status === 'pago' || !l.status))
        .reduce((acc, l) => acc + (l.valor || 0), 0);

      // Custos de insumos aplicados
      const custosInsumos = movInsumos
        .filter(m => String(m.safra_id) === String(safra.id))
        .reduce((acc, m) => acc + (Number(m.custo_total) || 0), 0);

      // Outros custos alocados
      const custosA = custosAlocados
        .filter(c => String(c.safra_id) === String(safra.id))
        .reduce((acc, c) => acc + (c.valor || 0), 0);

      const despesasTotal = despesasLancamentos + custosInsumos + custosA;
      const resultado = receitas - despesasTotal;
      const margem = receitas > 0 ? (resultado / receitas) * 100 : 0;

      // Produtividade (Entradas / 60)
      const producao = movGraos
        .filter(m => String(m.safra_id) === String(safra.id) && m.tipo === 'entrada')
        .reduce((acc, m) => acc + (Number(m.peso_liquido) || 0), 0) / 60;

      const area = Number(safra.area_plantada) || 1;
      const produtividadeHa = producao / area;

      const custoPorHa = despesasTotal / area;
      const receitaPorHa = receitas / area;
      const resultadoPorHa = resultado / area;

      return {
        ...safra,
        receitas,
        despesas: despesasTotal,
        resultado,
        margem,
        producao,
        produtividadeHa,
        custoPorHa,
        receitaPorHa,
        resultadoPorHa,
        area
      };
    });
  }, [safras, lancamentos, custosAlocados, movInsumos, movGraos]);

  // Filtrar safras
  const safrasFiltradas = safraSelecionada === "todas"
    ? rentabilidadeSafras
    : rentabilidadeSafras.filter(s => s.id === safraSelecionada);

  // Totais gerais
  const totais = useMemo(() => {
    const dados = safraSelecionada === "todas" ? rentabilidadeSafras : safrasFiltradas;
    return {
      receitas: dados.reduce((a, s) => a + s.receitas, 0),
      despesas: dados.reduce((a, s) => a + s.despesas, 0),
      resultado: dados.reduce((a, s) => a + s.resultado, 0),
      area: dados.reduce((a, s) => a + (s.area || 0), 0),
      producao: dados.reduce((a, s) => a + s.producao, 0)
    };
  }, [rentabilidadeSafras, safrasFiltradas, safraSelecionada]);

  // Dados para gráfico de comparação
  const dadosComparacao = rentabilidadeSafras.map(s => ({
    nome: s.nome?.length > 15 ? s.nome.substring(0, 15) + '...' : s.nome,
    receitas: s.receitas,
    despesas: s.despesas,
    resultado: s.resultado
  }));

  // Dados para gráfico de pizza (distribuição de custos)
  const distribuicaoCustos = useMemo(() => {
    const categoriasAgrupdas = {};

    const safrasIds = safraSelecionada === "todas"
      ? safras.map(s => s.id)
      : [safraSelecionada];

    lancamentos
      .filter(l => safrasIds.includes(l.safra_id) && l.tipo === 'despesa')
      .forEach(l => {
        const cat = l.categoria || 'Outros';
        categoriasAgrupdas[cat] = (categoriasAgrupdas[cat] || 0) + (l.valor || 0);
      });

    custosAlocados
      .filter(c => safrasIds.includes(c.safra_id))
      .forEach(c => {
        const cat = c.categoria || 'Custos Alocados';
        categoriasAgrupdas[cat] = (categoriasAgrupdas[cat] || 0) + (c.valor || 0);
      });

    return Object.entries(categoriasAgrupdas)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [lancamentos, custosAlocados, safraSelecionada, safras]);

  // Dados para radar de performance
  const performanceRadar = useMemo(() => {
    if (safrasFiltradas.length === 0) return [];

    const maxReceita = Math.max(...rentabilidadeSafras.map(s => s.receitaPorHa), 1);
    const maxProdutividade = Math.max(...rentabilidadeSafras.map(s => s.produtividadeHa), 1);
    const maxMargem = Math.max(...rentabilidadeSafras.map(s => Math.abs(s.margem)), 1);

    return safrasFiltradas.slice(0, 3).map(s => ({
      safra: s.nome?.substring(0, 10),
      'Receita/ha': (s.receitaPorHa / maxReceita) * 100,
      'Produtividade': (s.produtividadeHa / maxProdutividade) * 100,
      'Margem': ((s.margem + 100) / (maxMargem + 100)) * 100,
      'Eficiência': s.margem > 0 ? Math.min(s.margem, 100) : 0
    }));
  }, [safrasFiltradas, rentabilidadeSafras]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
          <p className="font-semibold text-stone-800 mb-2">{label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="p-6 border-b border-stone-200 bg-gradient-to-r from-green-600 to-emerald-700 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Wheat className="w-7 h-7" />
                Rentabilidade por Safra
              </h2>
              <p className="text-white/80 mt-1">{propriedade?.nome}</p>
            </div>
            <Select value={safraSelecionada} onValueChange={setSafraSelecionada}>
              <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Selecionar safra" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas as Safras</SelectItem>
                {safras.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPIs */}
        <div className="p-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
                <TrendingUp className="w-4 h-4" />
                Receita Total
              </div>
              <p className="text-xl font-bold text-green-700">{formatCurrency(totais.receitas)}</p>
            </CardContent>
          </Card>

          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-600 text-sm mb-1">
                <TrendingDown className="w-4 h-4" />
                Custo Total
              </div>
              <p className="text-xl font-bold text-red-700">{formatCurrency(totais.despesas)}</p>
            </CardContent>
          </Card>

          <Card className={`${totais.resultado >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-stone-600 text-sm mb-1">
                <DollarSign className="w-4 h-4" />
                Resultado
              </div>
              <p className={`text-xl font-bold ${totais.resultado >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                {formatCurrency(totais.resultado)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-purple-600 text-sm mb-1">
                <Layers className="w-4 h-4" />
                Área Total
              </div>
              <p className="text-xl font-bold text-purple-700">{totais.area.toFixed(1)} ha</p>
            </CardContent>
          </Card>

          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-amber-600 text-sm mb-1">
                <Scale className="w-4 h-4" />
                Resultado/ha
              </div>
              <p className="text-xl font-bold text-amber-700">
                {formatCurrency(totais.area > 0 ? totais.resultado / totais.area : 0)}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Comparação de Safras */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Comparativo de Safras
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dadosComparacao}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="nome" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis
                    tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(v)}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="receitas" name="Receitas" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="despesas" name="Despesas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Distribuição de Custos */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-600" />
              Distribuição de Custos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={distribuicaoCustos}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    labelLine={false}
                    label={renderPieLabel}
                  >
                    {distribuicaoCustos.map((entry, index) => (
                      <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabela Detalhada */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle>Análise Detalhada por Safra</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-stone-200 text-sm">
                  <th className="text-left py-3 px-3 font-semibold">Safra</th>
                  <th className="text-left py-3 px-3 font-semibold">Cultura</th>
                  <th className="text-right py-3 px-3 font-semibold">Área</th>
                  <th className="text-right py-3 px-3 font-semibold text-green-700">Receitas</th>
                  <th className="text-right py-3 px-3 font-semibold text-red-700">Custos</th>
                  <th className="text-right py-3 px-3 font-semibold">Resultado</th>
                  <th className="text-right py-3 px-3 font-semibold">Margem</th>
                  <th className="text-right py-3 px-3 font-semibold">R$/ha</th>
                </tr>
              </thead>
              <tbody>
                {safrasFiltradas.map(safra => (
                  <tr key={safra.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-3 px-3 font-medium">{safra.nome}</td>
                    <td className="py-3 px-3">
                      <Badge variant="secondary">{safra.cultura}</Badge>
                    </td>
                    <td className="py-3 px-3 text-right">{safra.area?.toFixed(1)} ha</td>
                    <td className="py-3 px-3 text-right text-green-600 font-medium">{formatCurrency(safra.receitas)}</td>
                    <td className="py-3 px-3 text-right text-red-600 font-medium">{formatCurrency(safra.despesas)}</td>
                    <td className={`py-3 px-3 text-right font-bold ${safra.resultado >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(safra.resultado)}
                    </td>
                    <td className="py-3 px-3 text-right">
                      <Badge className={safra.margem >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {safra.margem.toFixed(1)}%
                      </Badge>
                    </td>
                    <td className={`py-3 px-3 text-right font-medium ${safra.resultadoPorHa >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      {formatCurrency(safra.resultadoPorHa)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Cards de Performance Individual */}
      {safrasFiltradas.length > 0 && safrasFiltradas.length <= 3 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {safrasFiltradas.map(safra => (
            <Card key={safra.id} className="bg-white border-stone-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wheat className="w-5 h-5 text-green-600" />
                  {safra.nome}
                </CardTitle>
                <Badge variant="outline">{safra.cultura}</Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Margem de Lucro</span>
                    <span className={safra.margem >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {safra.margem.toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={Math.max(0, Math.min(100, safra.margem))} className="h-2" />
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-stone-50 rounded">
                    <p className="text-stone-500">Custo/ha</p>
                    <p className="font-semibold">{formatCurrency(safra.custoPorHa)}</p>
                  </div>
                  <div className="p-2 bg-stone-50 rounded">
                    <p className="text-stone-500">Receita/ha</p>
                    <p className="font-semibold">{formatCurrency(safra.receitaPorHa)}</p>
                  </div>
                  <div className="p-2 bg-stone-50 rounded">
                    <p className="text-stone-500">Produtividade</p>
                    <p className="font-semibold">{safra.produtividadeHa.toFixed(1)} sc/ha</p>
                  </div>
                  <div className="p-2 bg-stone-50 rounded">
                    <p className="text-stone-500">Status</p>
                    <Badge variant="secondary" className="text-xs">{safra.status}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
